import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, web, errMsg } from "../../lib/api";
import { WEEKDAYS, MON_FRI, MAX_DAYS, MAX_EVENTS, weeklyDates, addDays, ymd } from "../../lib/recurrence";
import { Calendar } from "../ui/calendar";
import { Btn } from "./kit";
import { Modal, Field, TextInput, TextArea, Chips } from "./Form";

const TYPES = [["game", "Game"], ["practice", "Practice"], ["other", "Other"]];
const MODES = [["once", "One date"], ["weekly", "Repeat weekly"], ["dates", "Pick dates"]];
const SCOPES = [["one", "This event only"], ["later", "This and later events"]];
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const pad = (n) => String(n).padStart(2, "0");
const localParts = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return { date: "", time: "" };
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};
const toIso = (date, time) => (date ? new Date(`${date}T${time || "00:00"}`).toISOString() : null);

const toForm = (e) => {
  const s = localParts(e?.start);
  return { type: e?.type ?? "game", title: e?.title ?? "", opponent: e?.opponent ?? "", date: s.date, time: s.time || "18:00", end_time: localParts(e?.end).time, location: e?.location ?? "", notes: e?.notes ?? "" };
};
const fresh = () => ({ mode: "once", days: MON_FRI, until: "", picked: [], scope: "one" });

const WeeklyPicker = ({ f, r, setR, count }) => (
  <div data-testid="repeat-weekly" className="space-y-3 rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4">
    <Field label="Repeat on">
      <Chips multi testid="repeat-day" options={WEEKDAYS} value={r.days} onChange={(days) => setR((x) => ({ ...x, days }))} />
    </Field>
    <div className="flex flex-wrap gap-2">
      <button type="button" data-testid="repeat-mon-fri" onClick={() => setR((x) => ({ ...x, days: MON_FRI }))} className="rounded-full border border-[#39FF14]/50 px-3 py-1.5 font-mono-gd text-xs uppercase text-[#39FF14] hover:bg-[#39FF14]/10">Mon–Fri</button>
      <button type="button" data-testid="repeat-every-day" onClick={() => setR((x) => ({ ...x, days: WEEKDAYS.map(([d]) => d) }))} className="rounded-full border border-[#1E293B] px-3 py-1.5 font-mono-gd text-xs uppercase text-slate-300 hover:text-white">Every day</button>
    </div>
    <Field label="Repeat until">
      <TextInput data-testid="repeat-until-input" type="date" value={r.until} min={f.date || undefined} max={f.date ? addDays(f.date, MAX_DAYS) : undefined} onChange={(e) => setR((x) => ({ ...x, until: e.target.value }))} />
    </Field>
    <p data-testid="repeat-count" className="font-mono-gd text-xs uppercase tracking-wider text-slate-300">{count} event{count === 1 ? "" : "s"} will be added</p>
  </div>
);

const DatesPicker = ({ r, setR }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const max = new Date(today);
  max.setDate(max.getDate() + MAX_DAYS);
  return (
    <div data-testid="repeat-dates" className="rounded-2xl border border-[#1E293B] bg-[#0F172A] p-2">
      <Calendar mode="multiple" selected={r.picked} onSelect={(picked) => setR((x) => ({ ...x, picked: picked ?? [] }))} fromDate={today} toDate={max}
        className="mx-auto w-fit text-white"
        classNames={{ day_selected: "bg-[#39FF14] text-[#0A0E1A] hover:bg-[#39FF14] hover:text-[#0A0E1A] focus:bg-[#39FF14]", day_today: "ring-1 ring-[#39FF14]/60", day_disabled: "text-slate-600 opacity-50", day_outside: "text-slate-600" }} />
      <p data-testid="repeat-picked-count" className="px-2 pb-2 font-mono-gd text-xs uppercase tracking-wider text-slate-300">{r.picked.length} date{r.picked.length === 1 ? "" : "s"} selected</p>
    </div>
  );
};

/** Add one event, a weekly repeat or several picked dates; edit one event or "this and later" in a series. */
export const EventForm = ({ open, onClose, teamId, event, series, level = "varsity", onSaved }) => {
  const [f, setF] = useState(toForm(event));
  const [r, setR] = useState(fresh());
  const [lv, setLv] = useState(level);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(toForm(event)); setR(fresh()); setLv(level); } }, [open, event, level]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const isGame = f.type === "game";
  const inSeries = !!(event && series?.id);
  const later = inSeries && r.scope === "later";
  const dates = useMemo(() => (r.mode === "weekly" ? weeklyDates(f.date, r.until, r.days) : r.mode === "dates" ? r.picked.map(ymd).sort() : [f.date]), [r, f.date]);

  const bodyFor = (date, title) => ({
    type: f.type, title, opponent: isGame ? f.opponent.trim() || null : null,
    start: toIso(date, f.time), end: f.end_time ? toIso(date, f.end_time) : null,
    timezone: event?.timezone ?? TZ, location: f.location.trim() || null, notes: f.notes.trim() || null,
  });

  const putLevel = (ids) => web.put(`/teams/${teamId}/schedule/levels`, { event_ids: ids, level: lv });
  const save = async (title) => {
    if (later) {
      const updates = series.events.filter((x) => new Date(x.start) >= new Date(event.start))
        .map((x) => ({ event_id: x.id, body: bodyFor(x.id === event.id ? f.date : localParts(x.start).date, title) }));
      const { data } = await web.post(`/teams/${teamId}/schedule/series/${series.id}/update`, { updates });
      await putLevel(updates.map((u) => u.event_id));
      return `${data?.updated ?? updates.length} events updated${data?.failed ? `, ${data.failed} failed` : ""}`;
    }
    if (event) { await api.patch(`/teams/${teamId}/schedule/${event.id}`, bodyFor(f.date, title)); if (lv !== level) await putLevel([event.id]); return "Event updated"; }
    if (dates.length > 1) {
      const { data } = await web.post(`/teams/${teamId}/schedule/series`, { events: dates.map((d) => bodyFor(d, title)), level: lv });
      return `${data?.created ?? dates.length} events added${data?.failed ? `, ${data.failed} failed` : ""}`;
    }
    const { data } = await api.post(`/teams/${teamId}/schedule`, bodyFor(dates[0], title));
    const id = data?.id ?? data?.event?.id;
    if (id && lv !== "varsity") await putLevel([id]);
    return "Event added to the schedule";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (r.mode !== "dates" && !f.date) return toast.error(r.mode === "weekly" ? "Pick the first date" : "Pick a date");
    if (isGame && !f.opponent.trim() && !f.title.trim()) return toast.error("Add the opponent");
    if (!isGame && !f.title.trim()) return toast.error("Add a title");
    if (r.mode === "weekly" && (!r.days.length || !r.until)) return toast.error("Pick the days and an end date");
    if (r.mode !== "once" && !dates.filter(Boolean).length) return toast.error("No dates match. Check the days and end date");
    if (dates.length > MAX_EVENTS) return toast.error(`That's more than ${MAX_EVENTS} events. Pick a shorter range`);
    setBusy(true);
    try {
      toast.success(await save(f.title.trim() || `vs ${f.opponent.trim()}`));
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const label = event ? (later ? "Save all later events" : "Save changes") : dates.length > 1 ? `Add ${dates.length} events` : "Add event";
  return (
    <Modal open={open} onClose={onClose} testid="event-form-dialog" title={event ? "Edit event" : "Add event"} description="Your team sees this in the GAMEDAY mobile app too.">
      <form onSubmit={submit} className="mt-2 space-y-4">
        {inSeries && <Field label="Apply changes to"><Chips testid="event-scope" options={SCOPES} value={r.scope} onChange={(scope) => setR((x) => ({ ...x, scope }))} /></Field>}
        <Field label="Schedule"><Chips testid="event-level" options={[["varsity", "Varsity"], ["jv", "JV"]]} value={lv} onChange={setLv} /></Field>
        <Field label="Type"><Chips testid="event-type" options={TYPES} value={f.type} onChange={set("type")} /></Field>
        {isGame && <Field label="Opponent"><TextInput data-testid="event-opponent-input" value={f.opponent} onChange={set("opponent")} placeholder="Lakewood Spartans" /></Field>}
        <Field label={isGame ? "Title (optional)" : "Title"}><TextInput data-testid="event-title-input" value={f.title} onChange={set("title")} placeholder={isGame ? `vs ${f.opponent || "Opponent"}` : "Team practice"} /></Field>
        {!event && <Field label="When"><Chips testid="event-mode" options={MODES} value={r.mode} onChange={(mode) => setR((x) => ({ ...x, mode }))} /></Field>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {r.mode !== "dates" && !later && <Field label={r.mode === "weekly" ? "First date" : "Date"} className="col-span-2 sm:col-span-1"><TextInput data-testid="event-date-input" type="date" value={f.date} onChange={set("date")} /></Field>}
          <Field label="Start"><TextInput data-testid="event-time-input" type="time" value={f.time} onChange={set("time")} /></Field>
          <Field label="End (optional)"><TextInput data-testid="event-end-input" type="time" value={f.end_time} onChange={set("end_time")} /></Field>
        </div>
        {later && <p data-testid="event-scope-later-hint" className="text-xs text-slate-400">Each later event keeps its own date. Type, title, time, location and notes change for all of them, replacing any one-off changes you made to later events.</p>}
        {r.mode === "weekly" && <WeeklyPicker f={f} r={r} setR={setR} count={dates.length} />}
        {r.mode === "dates" && <DatesPicker r={r} setR={setR} />}
        <Field label="Location"><TextInput data-testid="event-location-input" value={f.location} onChange={set("location")} placeholder="Home gym" /></Field>
        <Field label="Notes"><TextArea data-testid="event-notes-input" value={f.notes} onChange={set("notes")} placeholder="Bus leaves at 4:30" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={onClose} data-testid="event-form-cancel">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="event-form-save">{busy ? "Saving…" : label}</Btn>
        </div>
      </form>
    </Modal>
  );
};
